import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  InputGroup,
  InputLeftAddon,
  useColorModeValue,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useToastNotification } from 'utils/toast-utils';
import { useFormik } from 'formik';
import { createUserSchema } from 'views/auth/schemas';

const CreateUserModal = ({ isOpen, onClose, onSubmit }) => {
  const { showToast } = useToastNotification();

  const formik = useFormik({
    initialValues: {
      companyName: '',
      rif: '',
      email: '',
      phoneNumber: '',
    },
    validationSchema: createUserSchema,
    onSubmit: async (values) => {
      try {
        await onSubmit(values);
        formik.resetForm();
        onClose();
      } catch (error) {
        showToast(
          'Error',
          error.message || 'Failed to create user',
          'error'
        );
      }
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="4xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader sx={{ fontSize: "2rem" }} borderBottom="1px solid rgb(253, 250, 250)">
          Create User
        </ModalHeader>
        <ModalCloseButton top="5" right="5" />
        <ModalBody paddingTop="0" padding="2rem">
          <VStack spacing={4}>
            <HStack spacing={4} align="stretch" w="100%">
              <FormControl 
                isRequired 
                isInvalid={formik.touched.companyName && formik.errors.companyName}
              >
                <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                  Company Name
                </FormLabel>
                <Input
                  bg="white"
                  border="1px solid #D1D1D1"
                  height="48px"
                  borderRadius="6px"
                  name="companyName"
                  value={formik.values.companyName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Company Name"
                />
                <FormErrorMessage>{formik.errors.companyName}</FormErrorMessage>
              </FormControl>

              <FormControl 
                isRequired
                isInvalid={formik.touched.rif && formik.errors.rif}
              >
                <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                  RIF (Tax Identification Number)
                </FormLabel>
                <Input
                  bg="white"
                  border="1px solid #D1D1D1"
                  height="48px"
                  borderRadius="6px"
                  name="rif"
                  value={formik.values.rif}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="000000000"
                />
                <FormErrorMessage>{formik.errors.rif}</FormErrorMessage>
              </FormControl>
            </HStack>

            <HStack spacing={4} align="stretch" w="100%">
              <FormControl 
                isRequired
                isInvalid={formik.touched.email && formik.errors.email}
              >
                <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                  Email
                </FormLabel>
                <Input
                  bg="white"
                  border="1px solid #D1D1D1"
                  height="48px"
                  borderRadius="6px"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="email"
                />
                <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl 
                isRequired
                isInvalid={formik.touched.phoneNumber && formik.errors.phoneNumber}
              >
                <FormLabel color="#4E4E4E" fontSize="12px" fontWeight="400">
                  Phone number
                </FormLabel>
                <Input
                  bg="white"
                  border="1px solid #D1D1D1"
                  height="48px"
                  borderRadius="6px"
                  name="phoneNumber"
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="+58 412-1234567"
                />
                <FormErrorMessage>{formik.errors.phoneNumber}</FormErrorMessage>
              </FormControl>
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter gap="1rem">
          <Button
            color='white'
            fontSize="14px"
            fontWeight="400"
            width="90px"
            height="40px"
            borderRadius="6px"
            colorScheme="purple"
            onClick={formik.handleSubmit}
            isLoading={formik.isSubmitting}
            isDisabled={!formik.isValid || formik.isSubmitting}
          >
            Create
          </Button>
          <Button
            border="1px solid #E5E8EB"
            color='#475569'
            fontSize="14px"
            fontWeight="400"
            width="76px"
            height="40px"
            borderRadius="6px"
            variant="ghost"
            onClick={onClose}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateUserModal;
